import React, { ReactElement, useEffect } from "react";
import portraits, { defaultPortrait } from "../assets";
import { GameState } from "../types";
import { describeRecord } from "./history";
import "./VoteHistory.css";

type VoteHistoryTableProps = {
  gameState: GameState;
  user?: string;
};

/** Whether anything has been voted on yet, so there is something to show. */
export function hasVoteHistory(game: GameState): boolean {
  return (game.history ?? []).some((r) => r.votes !== undefined);
}

/**
 * A grid of how everybody voted, government by government: one row per government (oldest first), one
 * column per player in seat order. P and C mark the President and the Chancellor of that government.
 */
export function VoteHistoryTable(props: VoteHistoryTableProps): ReactElement {
  const game = props.gameState;
  const players = game.playerOrder;
  const records = (game.history ?? []).filter(
    (r) => r.votes !== undefined || r.outcome === "CHAOS_LIBERAL" || r.outcome === "CHAOS_FASCIST"
  );

  return (
    <div className="vh-scroll">
      <table className="vh-table">
        <thead>
          <tr>
            <th className="vh-gov-head">Government</th>
            {players.map((name) => {
              const icon = game.icon[name];
              const portrait = icon && icon !== defaultPortrait ? portraits[icon] : undefined;
              return (
                <th key={name} className={"vh-player" + (name === props.user ? " vh-you" : "")}>
                  {portrait ? <img src={portrait} alt="" /> : <span className="vh-avatar-empty">{name.charAt(0)}</span>}
                  <span className="vh-player-name">{name}</span>
                </th>
              );
            })}
            <th className="vh-result-head">Result</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record, i) => {
            const d = describeRecord(record);
            if (record.votes === undefined) {
              return (
                <tr key={i} className="vh-chaos">
                  <td colSpan={players.length + 2}>
                    Round {record.round}: three failed governments, so the top policy was enacted ({d.result.replace("Top card: ", "")}).
                  </td>
                </tr>
              );
            }
            return (
              <tr key={i}>
                <th className="vh-gov" scope="row">
                  <span className="vh-round">R{record.round}</span>
                  <span className="vh-gov-names">
                    {record.president} + {record.chancellor}
                  </span>
                </th>
                {players.map((name) => {
                  const vote = record.votes![name];
                  const role = name === record.president ? "P" : name === record.chancellor ? "C" : "";
                  return (
                    <td key={name} className={name === props.user ? "vh-you" : ""}>
                      <span
                        className={
                          "vh-cell " + (vote === undefined ? "vh-none" : vote ? "vh-ja" : "vh-nein")
                        }
                        aria-label={name + ": " + (vote === undefined ? "did not vote" : vote ? "Ja" : "Nein")}
                      >
                        {vote === undefined ? "·" : vote ? "Ja" : "Nein"}
                        {role && <sup className="vh-role">{role}</sup>}
                      </span>
                    </td>
                  );
                })}
                <td className="vh-result">
                  <span className={"vh-chip vh-chip-" + d.kind}>{d.result}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type VoteHistoryDialogProps = VoteHistoryTableProps & {
  onClose: () => void;
};

/** The vote history in a window over the game screen. */
export default function VoteHistoryDialog(props: VoteHistoryDialogProps): ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  return (
    <div className="vh-layer">
      <div className="vh-backdrop" onClick={props.onClose} />
      <div className="vh-panel" role="dialog" aria-modal="true" aria-label="Vote history">
        <div className="vh-head">
          <h2 className="vh-title">Vote history</h2>
          <button className="vh-close" aria-label="Close" onClick={props.onClose}>
            ×
          </button>
        </div>
        <VoteHistoryTable gameState={props.gameState} user={props.user} />
        <div className="vh-legend">
          <span className="vh-cell vh-ja">Ja</span> yes · <span className="vh-cell vh-nein">Nein</span> no ·{" "}
          <sup className="vh-role">P</sup> President · <sup className="vh-role">C</sup> Chancellor
        </div>
      </div>
    </div>
  );
}
